import React, { useState, useEffect } from 'react';
import './ToolPanel.css';
import { DRAWING_MODES, SPACE_TYPES } from '../utils/constants';

/**
 * ToolPanel component - Fixed panel of drawing tools for the floorplan viewer
 * 
 * @param {Object} props - Component props
 * @param {string} props.activeMode - Currently active drawing mode
 * @param {Function} props.toggleSelectMode - Function to toggle select mode
 * @param {Function} props.toggleSetScale - Function to toggle scale setting mode
 * @param {Function} props.toggleDrawBox - Function to toggle box drawing mode
 * @param {Function} props.toggleDrawShape - Function to toggle shape drawing mode
 * @param {Function} props.toggleAddEntry - Function to toggle entry point adding mode
 * @param {Function} props.resetView - Function to reset the view
 * @param {Function} props.setCurrentSpaceType - Function to set the current space type
 * @param {string} props.className - Optional className for custom styling
 * @returns {JSX.Element} The tool panel component
 */
const ToolPanel = ({ 
  activeMode, 
  toggleSelectMode, 
  toggleSetScale, 
  toggleDrawBox, 
  toggleDrawShape, 
  toggleAddEntry,
  resetView,
  setCurrentSpaceType,
  className = ''
}) => {
  // State to track if panel is minimized
  const [isMinimized, setIsMinimized] = useState(false);
  // State to track current space type
  const [currentSpaceType, setLocalSpaceType] = useState(SPACE_TYPES.DESK);
  
  /**
   * Prevent event propagation to stop unwanted box drawing
   * @param {React.MouseEvent} e - The mouse event
   */
  const stopPropagation = (e) => {
    e.stopPropagation();
  };
  
  /**
   * Handle button click with propagation stopping
   * @param {React.MouseEvent} e - The mouse event
   * @param {Function} handler - The click handler function
   */
  const handleButtonClick = (e, handler) => {
    e.stopPropagation();
    handler(e);
  };
  
  /**
   * Toggle minimized state
   * @param {React.MouseEvent} e - The mouse event
   */
  const toggleMinimized = (e) => {
    e.stopPropagation();
    setIsMinimized(prev => !prev);
  };
  
  /**
   * Set space type without changing drawing mode
   * @param {string} spaceType - Space type to set
   */
  const setSpaceType = (spaceType) => {
    if (setCurrentSpaceType) {
      setCurrentSpaceType(spaceType);
    }
    
    // Update local state for active highlighting
    setLocalSpaceType(spaceType);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      switch (e.key.toLowerCase()) {
        case 't':
        case 'h':
          setIsMinimized(prev => !prev);
          break;
        case 'v':
          toggleSelectMode();
          break;
        case 's':
          toggleSetScale();
          break;
        case 'b':
          toggleDrawBox();
          break;
        case 'f':
          toggleDrawShape();
          break;
        case 'e':
          toggleAddEntry();
          break;
        case 'c':
          resetView();
          break;
        // New space type shortcuts
        case 'p':
          setSpaceType(SPACE_TYPES.PHONE);
          // Only toggle box mode if not already in a drawing mode
          if (activeMode !== DRAWING_MODES.DRAWING_BOX && activeMode !== DRAWING_MODES.DRAWING_SHAPE) {
            toggleDrawBox();
          }
          break;
        case 'r':
          setSpaceType(SPACE_TYPES.ROOM);
          // Only toggle box mode if not already in a drawing mode
          if (activeMode !== DRAWING_MODES.DRAWING_BOX && activeMode !== DRAWING_MODES.DRAWING_SHAPE) {
            toggleDrawBox();
          }
          break;
        case 'd':
          setSpaceType(SPACE_TYPES.DESK);
          // Only toggle box mode if not already in a drawing mode
          if (activeMode !== DRAWING_MODES.DRAWING_BOX && activeMode !== DRAWING_MODES.DRAWING_SHAPE) {
            toggleDrawBox();
          }
          break;
        case 'x':
        case 'l': // Using 'l' as well since 'f' is already taken by free form
          setSpaceType(SPACE_TYPES.FLEX);
          // Only toggle box mode if not already in a drawing mode
          if (activeMode !== DRAWING_MODES.DRAWING_BOX && activeMode !== DRAWING_MODES.DRAWING_SHAPE) {
            toggleDrawBox();
          }
          break;
        default:
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSelectMode, toggleSetScale, toggleDrawBox, toggleDrawShape, toggleAddEntry, resetView, setCurrentSpaceType, activeMode]);
  
  return (
    <div 
      className={`tool-panel ${isMinimized ? 'minimized' : ''} ${className}`}
      onMouseDown={stopPropagation}
      onMouseMove={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
      onMouseOver={stopPropagation}
      onMouseEnter={stopPropagation}
      onMouseLeave={stopPropagation}
      onDragStart={stopPropagation}
      onDrag={stopPropagation}
      onDragEnd={stopPropagation}
    >
      <button 
        className="tool-button toggle-button"
        onClick={toggleMinimized}
        title={isMinimized ? "Show Tool Panel (T or H)" : "Hide Tool Panel (T or H)"}
      >
        {isMinimized ? (
          <>
            <span className="tool-button-text">Show</span>
            <span className="tool-button-icon">»</span>
          </>
        ) : (
          <>
            <span className="tool-button-icon">«</span>
            <span className="tool-button-text">Hide</span>
          </>
        )}
      </button>
      
      {!isMinimized && (
        <>
          <button 
            className={`tool-button ${activeMode === DRAWING_MODES.SELECT_MODE ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, toggleSelectMode)}
            title="Select Tool (V)"
          >
            <span className="tool-button-icon">👆</span>
            <span className="tool-button-text">Select</span>
            <span className="shortcut">V</span>
          </button>
          <button 
            className={`tool-button ${activeMode === DRAWING_MODES.SETTING_SCALE ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, toggleSetScale)}
            title="Set Scale (S)"
          >
            <span className="tool-button-icon">📐</span>
            <span className="tool-button-text">Scale</span>
            <span className="shortcut">S</span>
          </button>
          <button 
            className={`tool-button ${activeMode === DRAWING_MODES.DRAWING_BOX ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, toggleDrawBox)}
            title="Draw Box (B)"
          >
            <span className="tool-button-icon">📦</span>
            <span className="tool-button-text">Box</span>
            <span className="shortcut">B</span>
          </button>
          <button 
            className={`tool-button ${activeMode === DRAWING_MODES.DRAWING_SHAPE ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, toggleDrawShape)}
            title="Draw Free Form Shape (F)"
          >
            <span className="tool-button-icon">✏️</span>
            <span className="tool-button-text">Free Form</span>
            <span className="shortcut">F</span>
          </button>
          <button 
            className={`tool-button ${activeMode === DRAWING_MODES.ADDING_ENTRY ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, toggleAddEntry)}
            title="Add Entry Point (E)"
          >
            <span className="tool-button-icon">🚪</span>
            <span className="tool-button-text">Entry</span>
            <span className="shortcut">E</span>
          </button>
          
          {/* Space Type Buttons */}
          <div className="space-type-divider">Space Types</div>
          
          <button 
            className={`tool-button space-type-button ${(activeMode === DRAWING_MODES.DRAWING_BOX || activeMode === DRAWING_MODES.DRAWING_SHAPE) && currentSpaceType === SPACE_TYPES.DESK ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, () => setSpaceType(SPACE_TYPES.DESK))}
            title="Desk Space (D)"
          >
            <span className="tool-button-icon">
              <img src="/images/slides/desk.png" alt="Desk" className="space-type-icon" />
            </span>
            <span className="tool-button-text">Desk</span>
            <span className="shortcut">D</span>
          </button>
          
          <button 
            className={`tool-button space-type-button ${(activeMode === DRAWING_MODES.DRAWING_BOX || activeMode === DRAWING_MODES.DRAWING_SHAPE) && currentSpaceType === SPACE_TYPES.ROOM ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, () => setSpaceType(SPACE_TYPES.ROOM))}
            title="Meeting Room (R)"
          >
            <span className="tool-button-icon">
              <img src="/images/slides/meeting.png" alt="Room" className="space-type-icon" />
            </span>
            <span className="tool-button-text">Room</span>
            <span className="shortcut">R</span>
          </button>
          
          <button 
            className={`tool-button space-type-button ${(activeMode === DRAWING_MODES.DRAWING_BOX || activeMode === DRAWING_MODES.DRAWING_SHAPE) && currentSpaceType === SPACE_TYPES.PHONE ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, () => setSpaceType(SPACE_TYPES.PHONE))}
            title="Phone Booth (P)"
          >
            <span className="tool-button-icon">
              <img src="/images/slides/phonebooth.png" alt="Phone Booth" className="space-type-icon" />
            </span>
            <span className="tool-button-text">Phone</span>
            <span className="shortcut">P</span>
          </button>
          
          <button 
            className={`tool-button space-type-button ${(activeMode === DRAWING_MODES.DRAWING_BOX || activeMode === DRAWING_MODES.DRAWING_SHAPE) && currentSpaceType === SPACE_TYPES.FLEX ? 'active' : ''}`} 
            onClick={(e) => handleButtonClick(e, () => setSpaceType(SPACE_TYPES.FLEX))}
            title="Flex Space (X)"
          >
            <span className="tool-button-icon">
              <img src="/images/slides/flex.png" alt="Flex Space" className="space-type-icon" />
            </span>
            <span className="tool-button-text">Flex</span>
            <span className="shortcut">X</span>
          </button>
          {/* Reset view button - commented out for now
          <button 
            className="tool-button"
            onClick={(e) => handleButtonClick(e, resetView)}
            title="Reset View (Z)"
          >
            <span className="tool-button-icon">🔄</span>
            <span className="tool-button-text">Reset View</span>
            <span className="shortcut">Z</span>
          </button>
          */}
        </>
      )}
    </div>
  );
};

export default ToolPanel; 